import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Building2, Check, CheckCircle2, ChevronsUpDown, Globe2, Loader2, Sparkles, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { FEATURE_META } from "@/components/FeatureLocked";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

// Complete ISO 3166-1 country/territory coverage, alphabetical; "Other" pinned last.
const COUNTRIES = [
  "Afghanistan", "Åland Islands", "Albania", "Algeria", "American Samoa", "Andorra",
  "Angola", "Anguilla", "Antarctica", "Antigua and Barbuda", "Argentina", "Armenia",
  "Aruba", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh",
  "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bermuda", "Bhutan", "Bolivia",
  "Bonaire, Sint Eustatius and Saba", "Bosnia and Herzegovina", "Botswana", "Bouvet Island",
  "Brazil", "British Indian Ocean Territory", "Brunei Darussalam", "Bulgaria", "Burkina Faso",
  "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Cayman Islands",
  "Central African Republic", "Chad", "Chile", "China", "Christmas Island",
  "Cocos (Keeling) Islands", "Colombia", "Comoros", "Congo", "Congo (Democratic Republic)",
  "Cook Islands", "Costa Rica", "Côte d'Ivoire", "Croatia", "Cuba", "Curaçao", "Cyprus",
  "Czechia", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt",
  "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
  "Falkland Islands", "Faroe Islands", "Fiji", "Finland", "France", "French Guiana",
  "French Polynesia", "French Southern Territories", "Gabon", "Gambia", "Georgia", "Germany",
  "Ghana", "Gibraltar", "Greece", "Greenland", "Grenada", "Guadeloupe", "Guam", "Guatemala",
  "Guernsey", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Heard Island and McDonald Islands",
  "Holy See", "Honduras", "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran",
  "Iraq", "Ireland", "Isle of Man", "Israel", "Italy", "Jamaica", "Japan", "Jersey", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Korea (North)", "Korea (South)", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania",
  "Luxembourg", "Macao", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta",
  "Marshall Islands", "Martinique", "Mauritania", "Mauritius", "Mayotte", "Mexico",
  "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Montserrat", "Morocco",
  "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Caledonia",
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "Niue", "Norfolk Island", "North Macedonia",
  "Northern Mariana Islands", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama",
  "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Pitcairn", "Poland", "Portugal",
  "Puerto Rico", "Qatar", "Réunion", "Romania", "Russia", "Rwanda", "Saint Barthélemy",
  "Saint Helena", "Saint Kitts and Nevis", "Saint Lucia", "Saint Martin",
  "Saint Pierre and Miquelon", "Saint Vincent and the Grenadines", "Samoa", "San Marino",
  "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone",
  "Singapore", "Sint Maarten", "Slovakia", "Slovenia", "Solomon Islands", "Somalia",
  "South Africa", "South Georgia and the South Sandwich Islands", "South Sudan", "Spain",
  "Sri Lanka", "Sudan", "Suriname", "Svalbard and Jan Mayen", "Sweden", "Switzerland",
  "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tokelau",
  "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Turks and Caicos Islands",
  "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "US Minor Outlying Islands", "Uzbekistan", "Vanuatu", "Venezuela", "Vietnam",
  "Virgin Islands (British)", "Virgin Islands (US)", "Wallis and Futuna", "Western Sahara",
  "Yemen", "Zambia", "Zimbabwe",
  "Other", // catch-all, always last
];
const isID = (c) => ["indonesia", "id", "idn"].includes((c || "").trim().toLowerCase());
const fmt = (cur, v) => v == null ? null : (cur === "IDR" ? `Rp${Number(v).toLocaleString("id-ID")}` : `$${Number(v).toLocaleString("en-US")}`);

const STEPS = ["Market", "Plan", "Company", "Account"];
const INPUT = "w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [country, setCountry] = useState("");
  const [countryOpen, setCountryOpen] = useState(false);
  const [plans, setPlans] = useState(null);
  const [planId, setPlanId] = useState("");
  const [expandedPlans, setExpandedPlans] = useState({});
  const togglePlanFeatures = (id) => setExpandedPlans((s) => ({ ...s, [id]: !s[id] }));
  const [company, setCompany] = useState({ brand: "", legal: "" });
  const [account, setAccount] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const currency = isID(country) ? "IDR" : "USD";

  useEffect(() => {
    api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  const selectedPlan = (plans || []).find((p) => p.plan_id === planId);

  const next = () => {
    setError("");
    if (step === 1 && !country) return setError("Select your company country");
    if (step === 2 && !planId) return setError("Select a plan");
    if (step === 3 && !company.brand.trim()) return setError("Brand name is required");
    setStep((s) => Math.min(4, s + 1));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (account.password.length < 8) return setError("Password must be at least 8 characters");
    setLoading(true);
    try {
      await register({
        organization_brand_name: company.brand.trim(),
        organization_legal_name: company.legal.trim(),
        country, plan_id: planId,
        full_name: account.name.trim(), email: account.email.trim(), password: account.password,
      });
      toast.success("Welcome to PETRINOS Travel OS — your 14-day free trial is ready");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#070F21] p-6 text-white" data-testid="register-page">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(212,175,55,0.07),transparent_45%),radial-gradient(circle_at_85%_85%,rgba(30,64,175,0.25),transparent_45%)]" />
      <div className="relative mx-auto w-full max-w-2xl">
        <Link to="/" className="mb-6 flex items-center justify-center gap-3">
          <img src="/brand/petrinos-p-symbol.png" alt="PETRINOS" className="h-10 w-10 rounded-xl object-cover" />
          <div className="leading-tight">
            <div className="font-display text-lg font-extrabold">PETRINOS Travel OS</div>
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-400">Travel Operating System</div>
          </div>
        </Link>

        {/* step indicator */}
        <div className="mb-6 flex items-center justify-center gap-2" data-testid="register-steps">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const active = n === step, done = n < step;
            return (
              <div key={label} className="flex items-center gap-2">
                <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${active ? "bg-amber-400 text-[#0B1120]" : done ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 text-slate-400"}`} data-testid={`step-pill-${n}`}>
                  {done ? <CheckCircle2 className="h-3 w-3" /> : n} {label}
                </div>
                {n < 4 && <div className="h-px w-5 bg-slate-700" />}
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-2xl backdrop-blur">
          {/* STEP 1 — MARKET / COUNTRY */}
          {step === 1 && (
            <div data-testid="register-step-1">
              <div className="flex items-center gap-2 text-amber-300"><Globe2 className="h-5 w-5" /><h2 className="font-display text-xl font-bold text-white">Where is your company based?</h2></div>
              <p className="mt-1 text-sm text-slate-400">Select the country where your travel business is registered.</p>
              <div className="mt-5">
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Company country *</label>
                <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="country-select"
                      className="flex w-full items-center justify-between rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white hover:border-slate-500 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400">
                      <span className={country ? "" : "text-slate-500"}>{country || "Select your country…"}</span>
                      <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] border-slate-700 bg-slate-900 p-0 text-white" align="start">
                    <Command className="bg-slate-900">
                      <CommandInput placeholder="Search country…" data-testid="country-search" className="text-white" />
                      <CommandList className="bg-slate-900">
                        <CommandEmpty className="text-slate-400">No country found.</CommandEmpty>
                        <CommandGroup>
                          {COUNTRIES.map((c) => (
                            <CommandItem key={c} value={c} onSelect={() => { setCountry(c); setCountryOpen(false); }}
                              data-testid={`country-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
                              className="flex items-center justify-between text-slate-200 aria-selected:bg-slate-800 aria-selected:text-white">
                              <span className="flex items-center gap-2">
                                <Check className={`h-3.5 w-3.5 ${country === c ? "opacity-100 text-amber-300" : "opacity-0"}`} />
                                {c}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}

          {/* STEP 2 — PLAN */}
          {step === 2 && (
            <div data-testid="register-step-2">
              <div className="flex items-center gap-2 text-amber-300"><Sparkles className="h-5 w-5" /><h2 className="font-display text-xl font-bold text-white">Choose your plan</h2></div>
              <p className="mt-1 text-sm text-slate-400">Every plan starts with a <span className="font-semibold text-amber-300">14-day free trial</span>, then first-year promotional pricing, then the standard rate. Prices shown in {currency} for your market.</p>
              {plans === null ? (
                <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-300" /></div>
              ) : (
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {plans.map((p, idx) => {
                    const pr = p.pricing[currency] || {};
                    const promo = fmt(currency, pr.promo_monthly);
                    const std = fmt(currency, pr.standard_monthly);
                    // Feature presentation is driven by the backend entitlement keys
                    // (Pass 2). Additions = this tier's keys minus the previous tier's.
                    const keys = p.entitlement_keys || [];
                    const prevKeys = idx > 0 ? (plans[idx - 1].entitlement_keys || []) : [];
                    const additions = keys.filter((k) => !prevKeys.includes(k));
                    const featureLabel = (k) => FEATURE_META[k]?.label
                      || k.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
                    const highlights = idx === 0 ? keys.slice(0, 4) : additions.slice(0, 4);
                    const expanded = !!expandedPlans[p.plan_id];
                    const prevName = idx > 0 ? (plans[idx - 1].name || "").replace("PETRINOS ", "") : null;
                    const usersLabel = p.included_users ? `${p.included_users} users included` : "Unlimited users";
                    return (
                      <button key={p.plan_id} type="button" onClick={() => setPlanId(p.plan_id)} data-testid={`plan-card-${p.plan_id}`}
                        className={`rounded-xl border p-4 text-left transition-colors ${planId === p.plan_id ? "border-amber-400 bg-amber-400/10" : "border-slate-700 bg-slate-900 hover:border-slate-500"}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-display text-sm font-bold text-white">{p.name}</div>
                          {planId === p.plan_id && <CheckCircle2 className="h-4 w-4 text-amber-300" />}
                        </div>
                        <p className="mt-1 line-clamp-2 text-xs text-slate-400">{p.description}</p>
                        <div className="mt-3">
                          {promo ? (
                            <>
                              <div className="font-mono-tf text-lg font-extrabold text-amber-300" data-testid={`plan-promo-${p.plan_id}`}>{promo}<span className="text-xs font-medium text-slate-400">/mo · 1st year</span></div>
                              <div className="font-mono-tf text-xs text-slate-500">then {std}/mo standard</div>
                            </>
                          ) : std ? (
                            <div className="font-mono-tf text-lg font-extrabold text-amber-300" data-testid={`plan-price-${p.plan_id}`}>{std}<span className="text-xs font-medium text-slate-400">/mo</span></div>
                          ) : (
                            <div className="text-xs font-semibold text-slate-400" data-testid={`plan-tba-${p.plan_id}`}>{currency} pricing to be announced — trial is free</div>
                          )}
                        </div>
                        <div className="mt-3">
                          {prevName && (
                            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300/80" data-testid={`plan-inherits-${p.plan_id}`}>
                              Everything in {prevName}, plus:
                            </div>
                          )}
                          <div className="space-y-1">
                            {highlights.map((k) => (
                              <div key={k} className="flex items-start gap-1.5 text-[11px] text-slate-300"><CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-400" />{featureLabel(k)}</div>
                            ))}
                          </div>
                          {keys.length > highlights.length && (
                            <div role="button" tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); togglePlanFeatures(p.plan_id); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); togglePlanFeatures(p.plan_id); } }}
                              data-testid={`plan-features-toggle-${p.plan_id}`}
                              className="mt-2 cursor-pointer text-[11px] font-semibold text-amber-300 hover:underline">
                              {expanded ? "Hide full feature list" : `View all ${keys.length} features`}
                            </div>
                          )}
                          {expanded && (
                            <div className="mt-2 max-h-56 space-y-1 overflow-y-auto border-t border-slate-700 pt-2" data-testid={`plan-features-full-${p.plan_id}`}>
                              {keys.map((k) => {
                                const isNew = idx > 0 && additions.includes(k);
                                return (
                                  <div key={k} className="flex items-start gap-1.5 text-[11px] text-slate-300">
                                    <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-400" />
                                    <span>{featureLabel(k)}</span>
                                    {isNew && <span className="ml-auto shrink-0 rounded bg-amber-400/10 px-1 text-[8px] font-bold uppercase tracking-wider text-amber-300">New</span>}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-500" data-testid={`plan-trial-${p.plan_id}`}>{p.trial_days}-day free trial · {usersLabel}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — COMPANY DATA */}
          {step === 3 && (
            <div data-testid="register-step-3">
              <div className="flex items-center gap-2 text-amber-300"><Building2 className="h-5 w-5" /><h2 className="font-display text-xl font-bold text-white">Your company</h2></div>
              <p className="mt-1 text-sm text-slate-400">This becomes your Customer Organization workspace in PETRINOS.</p>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Brand name *</label>
                  <input value={company.brand} onChange={(e) => setCompany((c) => ({ ...c, brand: e.target.value }))} placeholder="e.g. Indotravelista" data-testid="register-org-brand" className={INPUT} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Legal company name (optional)</label>
                  <input value={company.legal} onChange={(e) => setCompany((c) => ({ ...c, legal: e.target.value }))} placeholder="e.g. PT Indotravelista Jelajah Buana" data-testid="register-org-legal" className={INPUT} />
                </div>
                <div className="rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-xs text-slate-300">
                  Country: <span className="font-semibold text-white">{country}</span> · Currency: <span className="font-mono-tf font-semibold text-amber-300">{currency}</span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4 — ACCOUNT */}
          {step === 4 && (
            <form onSubmit={submit} className="space-y-4" data-testid="register-step-4">
              <div className="flex items-center gap-2 text-amber-300"><User className="h-5 w-5" /><h2 className="font-display text-xl font-bold text-white">Create your admin account</h2></div>
              <p className="text-sm text-slate-400">You will be the Company Admin of this organization.</p>
              <input required value={account.name} onChange={(e) => setAccount((a) => ({ ...a, name: e.target.value }))} placeholder="Your full name" data-testid="register-name" className={INPUT} />
              <input required type="email" value={account.email} onChange={(e) => setAccount((a) => ({ ...a, email: e.target.value }))} placeholder="Work email" data-testid="register-email" className={INPUT} />
              <input required type="password" value={account.password} onChange={(e) => setAccount((a) => ({ ...a, password: e.target.value }))} placeholder="Password (min 8 characters)" data-testid="register-password" className={INPUT} />
              <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 px-3 py-2.5 text-xs text-amber-200" data-testid="trial-summary">
                <span className="font-semibold">14-day free trial</span> of <span className="font-semibold">{selectedPlan?.name}</span> — no charge today. After the trial, first-year promotional pricing applies, then the standard rate. Billed in {currency}.
              </div>
            </form>
          )}

          {error && <div className="mt-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-medium text-rose-300" data-testid="register-error">{error}</div>}

          <div className="mt-6 flex items-center justify-between">
            {step > 1 ? (
              <button type="button" onClick={() => { setError(""); setStep((s) => s - 1); }} data-testid="register-back" className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : <span />}
            {step < 4 ? (
              <button type="button" onClick={next} data-testid="register-next" className="flex items-center gap-1.5 rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-[#0B1120] hover:bg-amber-300">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button type="submit" onClick={submit} disabled={loading} data-testid="register-submit-button" className="flex items-center gap-2 rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-[#0B1120] hover:bg-amber-300 disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create account & start trial <ArrowRight className="h-4 w-4" /></>}
              </button>
            )}
          </div>

          <p className="mt-5 text-center text-sm text-slate-400">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login" className="font-semibold text-amber-300 hover:text-amber-200">Sign in</Link>
          </p>
        </div>
        <p className="mt-4 text-center text-[11px] text-slate-500">PETRINOS Travel OS · Operated by HelStèEn — PT Helsteen Syntra Teknologi</p>
      </div>
    </div>
  );
}
